import type { Conversation } from '../types';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faUserGear, faChevronDown, faBell, faGear, faLock, faTableCellsLarge } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';


type SidebarProps = {
  conversations: Conversation[];
  currentConversationId: string;
  dropdownOpen: boolean;
  toggleDropdown: () => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  onNewConversation: () => void;
  onConversationSelect: (conversationId: string) => void;
  onConversationMenuToggle: (conversationId: string, anchorRect: DOMRect) => void;
  onUserMenuToggle: (anchorRect: DOMRect) => void;
  activeConversationMenuId?: string | null;
  onVoiceClick?: () => void;
  onBetaAnnouncementClick?: () => void;
  selectedClientId?: string;
  onClientChange?: (clientId: string) => void;
  isClientLocked?: boolean;
  // Mobile responsive props
  isOpen?: boolean;
  onClose?: () => void;
};


const CLIENT_OPTIONS = [
  { id: 'dwa-internal', label: 'Digital Wolf Agency' },
  { id: 'morningstar', label: 'MorningStar Senior Living' },
  { id: 'plc', label: 'Priority Life Care' },
] as const;




// Daily and Weekly announcements hidden for beta
// Only showing "Welcome To Lupa - Beta" announcement

export const Sidebar: React.FC<SidebarProps> = ({
  conversations,
  currentConversationId,
  dropdownOpen,
  toggleDropdown,
  dropdownRef,
  onNewConversation,
  onConversationSelect,
  onConversationMenuToggle,
  onUserMenuToggle,
  activeConversationMenuId = null,
  onVoiceClick,
  onBetaAnnouncementClick,
  isOpen = true,
  onClose,
  selectedClientId = 'dwa-internal',
  onClientChange,
  isClientLocked = false,
}) => {
  // Filter conversations to show only active (non-archived)
  const activeConversations = conversations.filter((c) => !c.isArchived);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && onClose && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      <aside className={`
        flex flex-col p-6 shadow-[0_20px_45px_-20px_rgba(15,23,42,0.45)] overflow-visible
        fixed inset-y-0 left-0 z-50 h-full w-[85vw] max-w-[320px] rounded-r-[4px]
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:m-4 md:h-auto md:max-h-[calc(100%-2rem)] md:self-stretch md:w-80 md:max-w-none md:rounded-[4px] md:translate-x-0
      `}
      style={{
        background: 'rgba(25, 25, 25, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)'
      }}>
      {/* Top-left: back to the Hubs entryway — mirrors the microphone icon on the
          right. Dashboards used to be listed here; they now live in their Hubs. */}
      <Link
        to="/"
        className="absolute left-6 top-6 text-xl text-slate-600 cursor-pointer bg-transparent border-none p-1 rounded-full hover:bg-orange-100 transition-all duration-200"
        aria-label="All Hubs"
        title="All Hubs"
      >
        <FontAwesomeIcon
          icon={faTableCellsLarge}
          className="h-5 w-5 text-[#444444] transition-colors duration-200 hover:text-[var(--color-accent)]"
        />
      </Link>

      <button
        type="button"
        onClick={onVoiceClick}
        className="absolute right-6 top-6 text-xl text-slate-600 cursor-pointer bg-transparent border-none p-1 rounded-full hover:bg-orange-100 transition-all duration-200"
        aria-label="Open voice interface"
        title="Voice chat"
      >
        <FontAwesomeIcon
          icon={faMicrophone}
          className="h-5 w-5 text-[#444444] transition-colors duration-200 hover:text-[var(--color-accent)]"
        />
      </button>

      <div className="mb-10 flex flex-col items-center text-center">
        <div className="relative mb-5">
          <div className="brand-logo-circle brand-logo-circle-lg">
            <img
              src={BrandIcon}
              alt="Lupa profile"
            />
          </div>
          <div className="group absolute top-[72%] -right-3 -translate-y-1/2 md:top-[70%] md:-right-4 flex h-7 w-7 items-center justify-center rounded-full bg-[#444444] shadow-md transition-colors duration-200 hover:bg-[var(--color-accent)]">
            <FontAwesomeIcon
              icon={faBell}
              className="h-3 w-3 text-white transition-transform duration-200 group-hover:animate-ring-bell"
            />
          </div>
        </div>

        <h1 className="text-xl font-semibold text-[var(--color-accent)]">Lupa</h1>
        <p className="mt-1 mb-6 text-sm font-normal text-[#D4D4D4]">Digital Wolf Agency</p>

        <div ref={dropdownRef} className="relative w-full">
          {isClientLocked ? (
            <div
              className="flex w-full items-center justify-between rounded-[4px] border border-white/30 bg-white/40 px-4 py-2 text-sm font-medium text-slate-500"
              title="Client is locked for this conversation. Start a new chat to switch."
            >
              <span>{CLIENT_OPTIONS.find(c => c.id === selectedClientId)?.label ?? 'Select Client'}</span>
              <FontAwesomeIcon icon={faLock} className="h-3 w-3 text-slate-400" />
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={toggleDropdown}
                className="flex w-full items-center justify-between rounded-[4px] border border-white/60 bg-white/80 px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-orange-400 hover:bg-white hover:text-slate-800"
              >
                <span>{CLIENT_OPTIONS.find(c => c.id === selectedClientId)?.label ?? 'Select Client'}</span>
                <span className={`text-base transition-transform duration-150 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-[var(--color-accent)]" />
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 right-0 z-20 mt-2 overflow-hidden rounded-[4px] border border-slate-100 bg-slate-800 shadow-2xl">
                  <div className="py-1">
                    {CLIENT_OPTIONS.map(({ id, label }) => (
                      <button
                        type="button"
                        key={id}
                        onClick={() => { onClientChange?.(id); toggleDropdown(); }}
                        className={`w-full rounded-[4px] px-4 py-3 text-left text-sm transition ${
                          selectedClientId === id
                            ? 'bg-slate-700 text-white font-semibold'
                            : 'text-white/80 hover:bg-orange-500/90 hover:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onNewConversation}
        className="mb-4 flex w-full items-center justify-start rounded-[4px] bg-slate-800 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        Start a New Chat
      </button>

      <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-visible">
        <section>
          <div className="mb-3">
            <h3 className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#d4d4d4]">
              Announcements
            </h3>
            <div className="mt-1 h-[2px] w-full rounded-full bg-orange-400"></div>
          </div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={onBetaAnnouncementClick}
              className="relative flex w-full items-center rounded-[4px] bg-white/70 px-5 py-2 text-left text-sm font-medium text-slate-600 shadow-sm transition hover:bg-white"
            >
              Welcome To Lupa - Beta
            </button>
          </div>
        </section>

        <section className="flex min-h-0 flex-col">
          <div className="mb-3">
            <h3 className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#d4d4d4]">
              Past Conversations
            </h3>
            <div className="mt-1 h-[2px] w-full rounded-full bg-orange-400"></div>
          </div>
          <div className="hide-scrollbar flex-1 space-y-3 overflow-y-auto pr-1 overflow-visible">
            {activeConversations.length === 0 ? (
              <div className="rounded-[4px] bg-white/70 px-5 py-3 text-sm text-slate-500 shadow-sm">
                Start chatting to see conversations here.
              </div>
            ) : (
              activeConversations.map((conversation) => {
                const isActive = conversation.id === currentConversationId;
                const isMenuActive = activeConversationMenuId === conversation.id;
                // Show client label for all conversations; use selectedClientId for the active one
                const convClientId = isActive ? selectedClientId : conversation.clientId;
                const secondaryLabel = convClientId
                  ? (CLIENT_OPTIONS.find(c => c.id === convClientId)?.label ?? null)
                  : null;

                return (
                  <button
                    type="button"
                    key={conversation.id}
                    onClick={() => onConversationSelect(conversation.id)}
                    className={`group relative flex w-full items-start rounded-[4px] px-5 py-3 text-left text-sm shadow-sm transition ${
                      isActive
                        ? 'border border-orange-400 bg-white text-slate-800'
                        : 'border border-white/40 bg-white/70 text-slate-600 hover:bg-white'
                    }`}
                  >
                    <div className="flex w-full flex-col gap-1 text-left">
                      <span
                        className="font-medium text-slate-700 truncate"
                        title={conversation.title}
                      >
                        {conversation.title}
                      </span>
                      <div className="flex items-center justify-between gap-2">
                        {secondaryLabel && (
                          <span className="text-xs text-slate-500">{secondaryLabel}</span>
                        )}
                        <div className="flex items-center">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              event.stopPropagation();
                              const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
                              onConversationMenuToggle(conversation.id, rect);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                event.stopPropagation();
                                const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
                                onConversationMenuToggle(conversation.id, rect);
                              }
                            }}
                            className={`cursor-pointer rounded-full p-1 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                              isMenuActive
                                ? 'opacity-100 text-[#ea8266]'
                                : 'text-[#444444] opacity-0 group-hover:opacity-50 hover:opacity-100 group-hover:hover:opacity-100 hover:text-[#ea8266]'
                            }`}
                            aria-label="Conversation options"
                          >
                            <FontAwesomeIcon icon={faGear} className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        </div>

      <div className="mt-8 flex justify-start">
        <button
          type="button"
          className="flex items-center gap-2 rounded-[4px] bg-slate-800 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
          onClick={(event) => {
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
            onUserMenuToggle(rect);
          }}
        >
          <FontAwesomeIcon icon={faUserGear} className="text-lg" />
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
